import { SubFormType } from '@ikegps/core-api';
import {
	type CollectionModelFieldSubFormInstance,
	FormModel,
	type FormModelField,
	type FormModelFieldNestedList,
	type FormModelFieldSelectList,
	FormModelFieldSubForm,
	type IPoleForm,
	type ISpan,
	PoleFieldIds,
	SpanSubFormAdaptor,
	SpanType,
} from '..';
import { IkeId } from '../../id';
import { BaseFormFactory } from './BaseFormFactory';

/**
 * BaseForm is any FormModel which has a Pole SubForm field.
 *
 * TODO: Add field mappings to Pole SubForm type and use those instead of PoleFieldIds for field lookups.
 */
export class BaseForm implements IPoleForm {
	public static create(hasVerticalSeparationToken = false, hasMidSpanToken = false): BaseForm {
		const form = new FormModel();
		const id = IkeId.generate();
		form.state.id = id;
		form.state.revision = id;
		form.state.rootFormId = id;

		const factory = new BaseFormFactory(form, hasVerticalSeparationToken, hasMidSpanToken);
		factory.addBaseFormFields();
		factory.rootPoleSubForm();

		return factory.baseForm;
	}

	static isBaseForm(form: FormModel): boolean {
		return form.subFormType === SubFormType.POLE;
	}

	constructor(public form: FormModel) {
		if (!BaseForm.isBaseForm(form)) {
			throw new Error('BaseForm can only be constructed from a form with root subform type POLE');
		}
	}

	get poleField(): FormModelFieldSubForm {
		for (const field of [this.form, ...this.form.getFieldsRecursive]) {
			if (!FormModelFieldSubForm.isSubForm(field)) {
				continue;
			}
			if (field.subFormType !== SubFormType.POLE) {
				continue;
			}
			return field;
		}
		throw new Error('Pole SubForm field not found in FormModel');
	}

	get statusField(): FormModelFieldNestedList {
		return this.poleField.getField(PoleFieldIds.STATUS);
	}

	get sequenceField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.SEQUENCE);
	}

	get idField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.POLE_ID);
	}

	get tagPhotosField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.IMAGES);
	}

	get typeField(): FormModelFieldNestedList {
		return this.poleField.getField(PoleFieldIds.POLE_TYPE);
	}

	get ownerField(): FormModelFieldNestedList {
		return this.poleField.getField(PoleFieldIds.POLE_OWNER);
	}

	get circumferenceField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.CIRCUMFERENCE);
	}

	get tipField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.AGL);
	}

	get lowPowerAttachmentField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.LOW_POWER_ATTACHMENT);
	}

	get locationField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.GEOGRAPHIC_COORDINATE);
	}

	get streetAddressField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.STREET_ADDRESS);
	}

	get noteField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.NOTE);
	}

	get ikePhotoField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.IKE_PHOTO);
	}

	get lowPowerToCommField(): FormModelField {
		return this.poleField.getField(PoleFieldIds.LOW_POWER_TO_COMM);
	}

	get equipmentField(): FormModelFieldSubForm {
		return this.poleField.getField(PoleFieldIds.EQUIPMENT.FORM);
	}

	get anchorField(): FormModelFieldSubForm {
		return this.poleField.getField(PoleFieldIds.ANCHOR.FORM);
	}

	get spanField(): FormModelFieldSubForm {
		for (const field of this.form.getFieldsRecursive) {
			if (!FormModelFieldSubForm.isSubForm(field)) {
				continue;
			}
			if (field.subFormType !== SubFormType.SPAN) {
				continue;
			}
			return field;
		}
		throw new Error('Span SubForm field not found in FormModel');
	}

	get spanTypeField(): FormModelFieldSelectList {
		if (!this.spanField.subFormSpanTypeState) {
			throw new Error('Expected Span SubForm state to exist');
		}
		return this.spanField.getField(this.spanField.subFormSpanTypeState.typeFieldId);
	}

	get spanTypeMapping() {
		return {
			[SpanType.NextPole]: SpanType.PreviousPole,
			[SpanType.PreviousPole]: SpanType.NextPole,
			[SpanType.Other]: SpanType.Other,
		};
	}

	get spanVectorField(): FormModelField {
		if (!this.spanField.subFormSpanTypeState) {
			throw new Error('Expected Span SubForm state to exist');
		}
		return this.spanField.getField(this.spanField.subFormSpanTypeState.vectorFieldId);
	}

	get spanMidSpanIkePhoto() {
		if (!this.spanField.subFormSpanTypeState) {
			throw new Error('Expected Span SubForm state to exist');
		}

		if (!this.spanField.subFormSpanTypeState.midSpanIkePhotoFieldId) {
			throw new Error('Span is missing configured Mid Span IKE Photo field');
		}
		return this.spanField.getField(this.spanField.subFormSpanTypeState.midSpanIkePhotoFieldId);
	}

	get spanAttachmentHeightFields(): FormModelField[] {
		if (!this.spanField.subFormSpanTypeState) {
			throw new Error('Expected Span SubForm state to exist');
		}

		const field = this.spanWiresField.findField(PoleFieldIds.SPAN.WIRE.ATTACHMENT_HEIGHT);
		return field ? [field] : [];
	}

	spanValue(instance: CollectionModelFieldSubFormInstance): ISpan {
		return new SpanSubFormAdaptor(instance);
	}

	get spanWiresField(): FormModelFieldSubForm {
		return this.spanField.getField<FormModelFieldSubForm>(PoleFieldIds.SPAN.WIRE.FORM);
	}
}
